import { z } from 'zod';
import { isValidTimeZone } from './report-dto.js';
import { smartReportDateSchema } from './smart-report.js';
import { SLA_STATES, TICKET_SOURCES } from './ticket-dto.js';
import { TICKET_STATUSES } from './ticket-state-machine.js';
/** Which timestamp the date range applies to. */
export const TICKET_REPORT_DATE_BASES = ['created', 'resolved', 'closed', 'updated'];
export const TICKET_REPORT_PREVIEW_MAX_PAGE_SIZE = 100;
/** Export refuses (never truncates) above this many rows. */
export const TICKET_REPORT_EXPORT_MAX_ROWS = 50_000;
/** PDF is a summary-style table; refuse very large ones and point to Excel. */
export const TICKET_REPORT_PDF_MAX_ROWS = 5_000;
/**
 * Column allowlist. Free text (description, comments, resolution notes) is
 * deliberately absent: a report may be shared more widely than the ticket.
 */
export const TICKET_REPORT_COLUMNS = [
    { key: 'ticketNumber', label: 'Ticket' },
    { key: 'title', label: 'Title' },
    { key: 'status', label: 'Status' },
    { key: 'priority', label: 'Priority' },
    { key: 'category', label: 'Category' },
    { key: 'subcategory', label: 'Subcategory' },
    { key: 'source', label: 'Request type' },
    { key: 'requester', label: 'Requester' },
    { key: 'requesterEmail', label: 'Requester email' },
    { key: 'department', label: 'Department' },
    { key: 'location', label: 'Location' },
    { key: 'team', label: 'Team' },
    { key: 'technician', label: 'Technician' },
    { key: 'createdAt', label: 'Created' },
    { key: 'assignedAt', label: 'Assigned' },
    { key: 'firstResponseAt', label: 'First response' },
    { key: 'resolvedAt', label: 'Resolved' },
    { key: 'closedAt', label: 'Closed' },
    { key: 'updatedAt', label: 'Last updated' },
    { key: 'reopenCount', label: 'Reopened (count)' },
    { key: 'userConfirmation', label: 'User confirmation' },
    { key: 'slaState', label: 'SLA state' },
    { key: 'firstResponseDueAt', label: 'First response due' },
    { key: 'resolutionDueAt', label: 'Resolution due' },
    // Hotfix 34. Optional columns (not in the default set). The immediate-support mobile number is never a column.
    { key: 'immediateSupport', label: 'Immediate support requested' },
    { key: 'ipDeviceType', label: 'IP whitelisting device type' },
    { key: 'ipMacAddress', label: 'IP whitelisting MAC address' },
    { key: 'cctvRequestType', label: 'CCTV request type' },
    { key: 'cctvApprovalStatus', label: 'CCTV approval status' },
    { key: 'cctvAccessProvided', label: 'CCTV access provided' },
];
const COLUMN_KEYS = TICKET_REPORT_COLUMNS.map((c) => c.key);
export const TICKET_REPORT_DEFAULT_COLUMNS = [
    'ticketNumber', 'title', 'status', 'priority', 'category', 'requester', 'team', 'technician', 'createdAt', 'resolvedAt', 'closedAt', 'slaState',
];
const idList = z.array(z.string().uuid()).max(100);
export const ticketReportQuerySchema = z
    .object({
    date: smartReportDateSchema,
    dateBasis: z.enum(TICKET_REPORT_DATE_BASES).default('created'),
    statuses: z.array(z.enum(TICKET_STATUSES)).max(20).optional(),
    priorityIds: idList.optional(),
    categoryIds: idList.optional(),
    sources: z.array(z.enum(TICKET_SOURCES)).max(10).optional(),
    technicianIds: idList.optional(),
    requesterIds: idList.optional(),
    teamIds: idList.optional(),
    departmentIds: idList.optional(),
    locationIds: idList.optional(),
    slaStates: z.array(z.enum(SLA_STATES)).max(10).optional(),
    q: z.string().trim().min(1).max(50).optional(),
    columns: z.array(z.enum(COLUMN_KEYS)).min(1).max(COLUMN_KEYS.length).refine((c) => new Set(c).size === c.length, 'duplicate columns').default([...TICKET_REPORT_DEFAULT_COLUMNS]),
    page: z.number().int().min(1).max(100).default(1),
    pageSize: z.number().int().min(1).max(TICKET_REPORT_PREVIEW_MAX_PAGE_SIZE).default(25),
    tz: z.string().trim().min(1).max(64).refine(isValidTimeZone, { message: 'tz must be an IANA time zone' }).optional(),
})
    .strict();
//# sourceMappingURL=smart-report-ticket.js.map